import type { IterableEmbeddedMessage } from '@iterable/web-sdk'
import { DISMISS_ACTION_ID } from 'lib/iterable/constants'
import { HOME_DELIVERY_GUIDE_PAGE_PATH } from 'utils/constants'
import { areFlagOverridesAllowed } from './flagOverrides'

/**
 * Lets Dev Tools stand in a set of embedded messages so the announcement placement can be
 * reviewed without publishing an Iterable campaign.
 *
 * Gated on the *deployment* rather than on `isDevToolsEnabled`, for the same reason flag pins are
 * (see `flagOverrides`): Dev Tools are deliberately reachable in production via `?devTools=true`,
 * and a panel that could inject a fake clinical announcement there would be a live change to what
 * clinicians see, from a URL parameter.
 *
 * Preview messages never reach Iterable's API — not the dismiss event, not impressions. They carry
 * invented `messageId`s, so tracking them would write junk rows against campaigns that do not
 * exist and corrupt the Snowflake numbers this feature is supposed to produce.
 */

const STORAGE_KEY = 'devToolsAnnouncementPreview'

export const areAnnouncementPreviewsAllowed = areFlagOverridesAllowed

const dismissButton = (title = 'Got it') => ({
  id: 'preview-dismiss',
  title,
  action: { type: `action://${DISMISS_ACTION_ID}` },
})

function message(
  id: string,
  elements: NonNullable<IterableEmbeddedMessage['elements']>,
  payload?: Record<string, unknown>,
): IterableEmbeddedMessage {
  return { metadata: { messageId: id, placementId: 0, isProof: true }, elements, ...(payload ? { payload } : {}) }
}

/**
 * The shapes a published campaign can actually take. Ordered from "what the design draws" to the
 * awkward ones, because the awkward ones are the reason this panel exists — no real campaign will
 * ever be authored with a missing title or an unknown action id, and those are exactly the cases
 * that break a renderer.
 */
export const ANNOUNCEMENT_FIXTURES: Record<string, IterableEmbeddedMessage[]> = {
  'As designed': [
    message('preview-designed', {
      title: 'Your Texas license expires Nov 20, 2026',
      body: 'Renew it soon in order to keep seeing patients in Texas.',
      buttons: [dismissButton()],
    }),
  ],

  // A campaign picks its leading icon through the custom payload; `truck-fast` is the Home Delivery
  // alert (Figma 52306:154846). Its guide is the relative Open URL a campaign uses, which opens the
  // guide page in a new tab and so shows the external-link affordance. The "as designed" fixtures use Figma's copy and button order (dismiss
  // left of the CTA); both are campaign data, so a real campaign may differ.
  'Home Delivery (truck icon, guide in a new tab)': [
    message(
      'preview-home-delivery',
      {
        title: 'Prescribing for Home Delivery',
        body: 'Patients can now request home delivery at booking. When they do, prescribe from Order Set in ScriptSure. This guide walks through each step.',
        buttons: [
          dismissButton(),
          {
            id: 'preview-open-guide',
            title: 'Open guide',
            action: { type: 'openUrl', data: HOME_DELIVERY_GUIDE_PAGE_PATH },
          },
        ],
      },
      { icon: 'truck-fast' },
    ),
  ],

  // Figma 52306:154818: several announcements drawn as one card, a divider between rows.
  'As designed, several (grouped)': [
    message('preview-grouped-license', {
      title: 'Your Texas license expires Nov 20, 2026',
      body: 'Renew it soon in order to keep seeing patients in Texas.',
      buttons: [dismissButton()],
    }),
    message('preview-grouped-2fa', {
      title: 'Set up 2FA by Dec 12, 2026',
      body: 'It is required to set up multi-factor authentication for all providers in order to see patients.',
      buttons: [dismissButton()],
    }),
    message(
      'preview-grouped-home-delivery',
      {
        title: 'Prescribing for Home Delivery',
        body: 'Patients can now request home delivery at booking. When they do, prescribe from Order Set in ScriptSure. This guide walks through each step.',
        buttons: [
          dismissButton('Dismiss'),
          {
            id: 'preview-open-guide',
            title: 'Open guide',
            action: { type: 'openUrl', data: HOME_DELIVERY_GUIDE_PAGE_PATH },
          },
        ],
      },
      { icon: 'truck-fast' },
    ),
  ],

  // Iterable returns a *list* per placement, and nothing stops Marketing running two campaigns at
  // once against the same placement. These are the cases that prove the placement stacks rather
  // than rendering only the first.
  'Two messages': [
    message('preview-two-a', {
      title: 'Your Texas license expires Nov 20, 2026',
      body: 'Renew it soon in order to keep seeing patients in Texas.',
      buttons: [dismissButton()],
    }),
    message('preview-two-b', {
      title: 'New: async visits are live in Ohio',
      body: 'You can now claim async visits from the dashboard.',
      buttons: [dismissButton('Dismiss')],
    }),
  ],

  'Three messages': [
    message('preview-three-a', { title: 'Scheduled maintenance Sunday 02:00–04:00 ET', buttons: [dismissButton()] }),
    message('preview-three-b', {
      title: 'Your Texas license expires Nov 20, 2026',
      body: 'Renew it soon in order to keep seeing patients in Texas.',
      buttons: [dismissButton()],
    }),
    message('preview-three-c', {
      title: 'Updated controlled-substance policy',
      body: 'Review the changes before your next visit.',
      buttons: [dismissButton('Read it')],
    }),
  ],

  'Title only': [message('preview-title-only', { title: 'Scheduled maintenance Sunday 02:00–04:00 ET' })],

  'Body only': [message('preview-body-only', { body: 'A short announcement with no heading, and nothing to click.' })],

  'Two buttons': [
    message('preview-two-buttons', {
      title: 'Updated controlled-substance policy',
      body: 'Review the changes before your next visit.',
      buttons: [
        { id: 'preview-read', title: 'Read the policy', action: { type: 'openUrl', data: 'https://quick.md' } },
        dismissButton(),
      ],
    }),
  ],

  'Long content': [
    message('preview-long', {
      title:
        'Your Texas controlled-substance registration, your DEA registration and your state medical license all expire within the next thirty days',
      body: 'Renew all three soon in order to keep seeing patients in Texas without an interruption to your schedule, your pending visits or your async queue. Reach out to credentialing if any of them are already in progress and you are not sure where they stand.',
      buttons: [
        { id: 'preview-renew', title: 'Start renewal', action: { type: 'openUrl', data: 'https://quick.md' } },
        dismissButton(),
      ],
    }),
  ],

  // Marketing authored an id engineering never registered. The announcement must still render and
  // the click must no-op, rather than throwing and taking the dashboard with it.
  'Unknown action id': [
    message('preview-unknown-action', {
      title: 'Button points at an action we never registered',
      body: 'The announcement should still render; the click should do nothing.',
      buttons: [{ id: 'preview-bad', title: 'Go somewhere', action: { type: 'action://not-a-real-action' } }],
    }),
  ],

  // No buttons at all means no dismiss affordance, which would pin the banner permanently — the
  // placement is expected to supply its own fallback.
  'No buttons (undismissable)': [
    message('preview-no-buttons', {
      title: 'Published with no buttons',
      body: 'Nothing here dismisses it, so the placement has to add its own.',
    }),
  ],

  // The same trap, one step less obvious: there IS a button, it just does not dismiss. The
  // fallback has to appear here too, or the banner is pinned.
  'Only a link button (no dismiss)': [
    message('preview-link-only', {
      title: 'Published with a link but no dismiss',
      body: 'The link should work and a dismiss control should be added alongside it.',
      buttons: [
        { id: 'preview-link', title: 'Read the policy', action: { type: 'openUrl', data: 'https://quick.md' } },
      ],
    }),
  ],
}

export const ANNOUNCEMENT_FIXTURE_NAMES = Object.keys(ANNOUNCEMENT_FIXTURES)

/** The preview messages currently pinned from Dev Tools. Always empty where production is served. */
export function getAnnouncementPreview(): IterableEmbeddedMessage[] {
  if (!areAnnouncementPreviewsAllowed()) return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    // Hand-validated rather than asserted: this is parsed from storage a previous build wrote, so
    // a stale shape must be dropped rather than handed to the renderer as if it were a message.
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isEmbeddedMessageish)
  } catch {
    return []
  }
}

function isEmbeddedMessageish(value: unknown): value is IterableEmbeddedMessage {
  if (typeof value !== 'object' || value === null || !('metadata' in value)) return false
  const { metadata } = value
  return typeof metadata === 'object' && metadata !== null && 'messageId' in metadata
}

export function setAnnouncementPreview(messages: IterableEmbeddedMessage[]): void {
  if (!areAnnouncementPreviewsAllowed()) return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
}

export function clearAnnouncementPreview(): void {
  window.localStorage.removeItem(STORAGE_KEY)
}
